import React from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Typography, Empty, Button, Badge, Dropdown } from 'antd';
import {
  BellOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { markAsRead, markAllAsRead, removeNotification, Notification } from '../../store/slices/notificationsSlice';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Text } = Typography;

interface NotificationsPanelProps {
  isMobile?: boolean;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notifications, unreadCount } = useAppSelector(state => state.notifications);

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: 'var(--color-warning)' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />;
      default:
        return <InfoCircleOutlined style={{ color: 'var(--color-info)' }} />;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    dispatch(markAsRead(notification.id));

    // Navigate if there's an action URL
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllAsRead());
  };

  const handleRemoveNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    dispatch(removeNotification(notificationId));
  };

  const notificationsList = (
    <div style={{ width: isMobile ? 320 : 380, maxHeight: 500, overflow: 'auto' }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: 'var(--bg-primary)',
        zIndex: 1
      }}>
        <Text strong style={{ fontSize: '16px' }}>Notificaciones</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllRead}>
            Marcar todo como leído
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Empty
          image={<BellOutlined style={{ fontSize: 48, color: 'var(--border-color-strong)' }} />}
          description="No hay notificaciones"
          style={{ padding: '40px 20px' }}
        />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(notification) => (
            <List.Item
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              style={{
                cursor: notification.actionUrl ? 'pointer' : 'default',
                backgroundColor: notification.read ? 'var(--bg-primary)' : 'var(--bg-success-light)',
                borderLeft: notification.read ? 'none' : '3px solid var(--color-success)',
                padding: '12px 16px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (notification.actionUrl) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = notification.read ? 'var(--bg-primary)' : 'var(--bg-success-light)';
              }}
              actions={[
                <Button
                  key="delete"
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => handleRemoveNotification(e, notification.id)}
                  danger
                />
              ]}
            >
              <List.Item.Meta
                avatar={getIcon(notification.type)}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Text strong style={{ fontSize: '14px' }}>
                      {notification.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                      {dayjs(notification.timestamp).fromNow()}
                    </Text>
                  </div>
                }
                description={
                  <Text style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {notification.message}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => notificationsList}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined />}
          size="large"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
            width: '40px',
          }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationsPanel;
